import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Mutex } from "async-mutex";
import { MeilisearchResult } from "domains/search/interfaces/Search";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { removeCuFromType } from "infra/api/ExcludeCU";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  usePostEmbedInferencesMutation,
} from "infra/api/generated/api";
import _ from "lodash";

import {
  instantMeiliSearch,
  InstantMeiliSearchObject,
} from "@meilisearch/instant-meilisearch";

const mutex = new Mutex();

interface SearchProviderProps {
  getAllIndexes: (type: "asset" | "model") => {
    [key: string]: string | undefined;
  };
  getIndexes: (
    type: "asset" | "model",
    opts?: { privacy?: ("public" | "private")[] }
  ) => string[];
  searchWithTokenRenewal: <
    T extends
      | GetAssetsByAssetIdApiResponse["asset"]
      | GetModelsByModelIdApiResponse["model"] = GetAssetsByAssetIdApiResponse["asset"]
  >(
    ...args: Parameters<InstantMeiliSearchObject["searchClient"]["search"]>
  ) => Promise<
    | {
        results: MeilisearchResult<T>[];
      }
    | undefined
  >;
  getQueryEmbedding: (query: string) => Promise<
    | {
        query: string;
        embedding: number[];
      }
    | undefined
  >;
  clearCache: () => void;
  getIsPublicIndex: (index: string) => boolean;
}

export const SearchContext = createContext<SearchProviderProps>({
  getAllIndexes: () => ({}),
  getIndexes: () => [],
  searchWithTokenRenewal: async () => undefined,
  getQueryEmbedding: async () => undefined,
  clearCache: () => {},
  getIsPublicIndex: () => false,
});

export function SearchProvider({
  children = <></>,
}: {
  children?: React.ReactNode;
}) {
  const { refetchTeamDetails, selectedTeamDetails, selectedProject } =
    useTeamContext();
  const [config, setConfig] = useState<
    | {
        searchClient: InstantMeiliSearchObject["searchClient"];
        teamId: string;
      }
    | undefined
  >();
  const queryEmbeddingsRef = useRef<{
    [key: string]: number[];
  }>({});
  const [getEmbeddingTrigger] = usePostEmbedInferencesMutation();

  useEffect(() => {
    if (!selectedTeamDetails?.tenantToken) return;
    const { searchClient } = instantMeiliSearch(
      process.env.NEXT_PUBLIC_MEILISEARCH_URL ?? "",
      selectedTeamDetails.tenantToken
    );
    setConfig({ searchClient, teamId: selectedTeamDetails.id });
  }, [selectedTeamDetails]);

  const searchClient = useMemo(
    () =>
      config && config.teamId === selectedTeamDetails?.id
        ? config.searchClient
        : undefined,
    [config, selectedTeamDetails?.id]
  );

  /**
   * This function is used to search the meilisearch client.
   *
   * When it catches an error for Tenant token expiration
   *
   * @note see Sentry {@link https://scenario3d.sentry.io/issues/6372645289/?project=4504254817370112&referrer=github_integration}
   *
   */
  const searchWithTokenRenewal = useCallback(
    async <
      T extends
        | GetAssetsByAssetIdApiResponse["asset"]
        | GetModelsByModelIdApiResponse["model"] = GetAssetsByAssetIdApiResponse["asset"]
    >(
      ...args: Parameters<InstantMeiliSearchObject["searchClient"]["search"]>
    ): Promise<
      | {
          results: MeilisearchResult<T>[];
        }
      | undefined
    > => {
      if (!searchClient) return;
      try {
        return await searchClient.search(...args);
      } catch (error: any) {
        if (
          typeof error?.message === "string" &&
          error.message.includes("Tenant token expired")
        ) {
          await refetchTeamDetails();
        } else {
          throw error;
        }
      }
    },
    [searchClient, refetchTeamDetails]
  );

  const getAllIndexes = useCallback(
    (type: "asset" | "model") => {
      if (type === "model") {
        return {
          private: selectedTeamDetails?.searchIndexes?.models?.private,
          public: selectedTeamDetails?.searchIndexes?.models?.public,
        };
      }
      return {
        private: selectedTeamDetails?.searchIndexes?.assets?.private,
        public: selectedTeamDetails?.searchIndexes?.assets?.public,
      };
    },
    [selectedTeamDetails]
  );

  const getIndexes = useCallback(
    (
      type: "asset" | "model",
      {
        privacy = ["public", "private"],
      }: { privacy?: ("public" | "private")[] } = {
        privacy: ["public", "private"],
      }
    ) => {
      const indexes = _.pick(getAllIndexes(type), privacy);
      return _.compact(_.values(indexes));
    },
    [getAllIndexes]
  );

  const getIsPublicIndex = useCallback(
    (index: string) => {
      const publicIndexes = [
        getAllIndexes("asset").public,
        getAllIndexes("model").public,
      ];
      return publicIndexes.includes(index.split(":")[0]);
    },
    [getAllIndexes]
  );

  const getQueryEmbedding = useCallback(
    async (query: string) => {
      await mutex.acquire();
      if (queryEmbeddingsRef.current[query]) {
        mutex.release();
        return {
          query,
          embedding: queryEmbeddingsRef.current[query],
        };
      }
      try {
        const data = await getEmbeddingTrigger({
          projectId: selectedProject.id,
          body: {
            text: query,
          },
        })
          .unwrap()
          .then(removeCuFromType);
        queryEmbeddingsRef.current[query] = data.embedding;
      } catch (_) {
      } finally {
        mutex.release();
      }
      return {
        query,
        embedding: queryEmbeddingsRef.current[query],
      };
    },
    [getEmbeddingTrigger, selectedProject.id]
  );

  const clearCache = useCallback(() => {
    if (searchClient) {
      searchClient.clearCache();
    }
  }, [searchClient]);

  return (
    <SearchContext.Provider
      value={{
        getAllIndexes,
        getIndexes,
        searchWithTokenRenewal,
        getQueryEmbedding,
        clearCache,
        getIsPublicIndex,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext() {
  return useContext<SearchProviderProps>(SearchContext);
}
